import { useEffect, useState } from 'react';
import { Button } from '../Button';
import { diceCall } from '../../lib/diceRoll';
import type { DiceFace } from '../../lib/diceRoll';
import { StrategyHeading } from './StrategyHeading';
import comparisonStyles from './Btc15ModelComparison.module.css';
import styles from './Btc15DiceRoll.module.css';

/** Matches the CSS `tumble` keyframes' duration in Btc15DiceRoll.module.css. */
export const ROLL_DURATION_MS = 1400;

/** Inverse of each face's own placement transform — rotating the cube by
 * this brings that face to rest directly facing the viewer. */
const REST_TRANSFORM: Record<DiceFace, string> = {
  1: 'rotateX(0deg) rotateY(0deg)',
  2: 'rotateY(-90deg)',
  3: 'rotateX(-90deg)',
  4: 'rotateX(90deg)',
  5: 'rotateY(90deg)',
  6: 'rotateY(180deg)',
};

/** Which of the 3x3 grid cells (1-9, row-major) carry a pip, per face value. */
const PIP_LAYOUT: Record<DiceFace, number[]> = {
  1: [5],
  2: [1, 9],
  3: [1, 5, 9],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9],
};

function Face({ value, className }: { value: DiceFace; className: string }) {
  const active = new Set(PIP_LAYOUT[value]);
  return (
    <div className={className}>
      {Array.from({ length: 9 }, (_, i) => (
        <span key={i} className={active.has(i + 1) ? styles.pip : styles.pipEmpty} />
      ))}
    </div>
  );
}

/**
 * A literal 3D six-sided die, rendered inside Model comparison's compact
 * "Reference baselines" row (see Btc15ModelComparison) — deliberately
 * smaller and dashed-bordered rather than a full peer column, since it's a
 * reference to compare V1-V4 against, not a model in its own right. It
 * automatically tumbles and settles once per round, on whatever `face` the
 * caller already rolled for this round's tracked prediction (see
 * `predictDice()` in modelPredictions.ts) — this component only animates
 * that roll, it never generates its own. `face` is null until the first
 * round's prediction is ready; passing a new face (a new round) re-triggers
 * the tumble.
 *
 * The "Reroll" button replays that same tumble again — it does not draw a
 * new face. The tracked prediction is one real roll per round; letting
 * people watch it land again is purely cosmetic, so it can't ever disagree
 * with what Model comparison / Track record actually recorded for this round.
 */
export function Btc15DiceRoll({ face }: { face: DiceFace | null }) {
  const [displayFace, setDisplayFace] = useState<DiceFace | null>(null);
  const [rolling, setRolling] = useState(false);
  const [replayCount, setReplayCount] = useState(0);

  useEffect(() => {
    if (face === null) return;
    setRolling(true);
    const timer = window.setTimeout(() => {
      setDisplayFace(face);
      setRolling(false);
    }, ROLL_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [face, replayCount]);

  const call = displayFace && !rolling ? diceCall(displayFace) : null;

  return (
    <div className={`${comparisonStyles.column} ${comparisonStyles.columnSecondary}`}>
      <div className={comparisonStyles.columnHeader}>
        <StrategyHeading strategy="dice" />
      </div>

      <div className={styles.slideTrack}>
        <div className={styles.scene}>
          <div
            className={rolling ? `${styles.cube} ${styles.rolling}` : styles.cube}
            style={
              !rolling && displayFace
                ? { transform: REST_TRANSFORM[displayFace] }
                : undefined
            }
            data-testid="dice-cube"
          >
            <Face value={1} className={`${styles.face} ${styles.faceFront}`} />
            <Face value={6} className={`${styles.face} ${styles.faceBack}`} />
            <Face value={2} className={`${styles.face} ${styles.faceRight}`} />
            <Face value={5} className={`${styles.face} ${styles.faceLeft}`} />
            <Face value={3} className={`${styles.face} ${styles.faceTop}`} />
            <Face value={4} className={`${styles.face} ${styles.faceBottom}`} />
          </div>
        </div>
      </div>

      <div className={styles.resultRow}>
        <Button
          onClick={() => setReplayCount((n) => n + 1)}
          disabled={rolling || face === null}
        >
          {rolling ? 'Rolling…' : 'Reroll'}
        </Button>
        {!rolling && !call && <span className={styles.status}>Waiting for a round…</span>}
        {call && (
          <span
            className={
              call === 'OVER'
                ? `${styles.call} ${styles.callOver}`
                : `${styles.call} ${styles.callUnder}`
            }
          >
            Rolled {displayFace} → {call === 'OVER' ? '↗ UP' : '↘ DOWN'}
          </span>
        )}
      </div>
    </div>
  );
}
